import { motion } from "framer-motion";
import { Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import { useState } from "react";
import { Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { forgotPassword, resetPassword } from "../../../services/auth.api";

/**
 * Final step of the password reset. The API takes the phone, the 6-digit code
 * and the new password in one request, so the code is entered here rather than
 * exchanged for a reset token first.
 */
const ResetPasswordForm = ({ phone, onBack }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({
    mode: "all",
    defaultValues: { otp: "", password: "", confirmPassword: "" },
  });

  const password = watch("password");

  const containerVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, x: -50, transition: { duration: 0.4 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.3 } }),
  };

  const primaryColor = "#1B6DC1";

  const onSubmit = async (data) => {
    try {
      await resetPassword({ phone, otp: data.otp, password: data.password });
      toast.success("Password updated. Please sign in.");
      navigate("/authentication/login");
    } catch (error) {
      setError("root", { message: error?.message || "Failed to reset password" });
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await forgotPassword({ phone });
      toast.success("A new code is on its way.");
    } catch (error) {
      setError("root", { message: error?.message || "Failed to resend the code" });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="d-flex flex-column p-1 p-lg-4 gap-2"
    >
      <motion.div custom={1} variants={itemVariants}>
        <h2 className="text-center fw-bold text-dark" style={{ fontSize: "1.875rem" }}>
          RESET PASSWORD
        </h2>
        <p className="text-center text-secondary mt-2">
          Enter the code we sent you and choose a new password.
        </p>
        {phone && <p className="text-center text-muted small mb-0">{phone}</p>}
      </motion.div>

      <motion.form
        custom={2}
        variants={itemVariants}
        onSubmit={handleSubmit(onSubmit)}
        className="d-flex flex-column gap-2"
      >
        {errors.root && (
          <div className="alert alert-danger py-2 mb-0" role="alert">
            {errors.root.message}
          </div>
        )}

        {/* OTP */}
        <div>
          <InputGroup>
            <InputGroup.Text
              className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.otp ? "border-danger" : ""}`}
            >
              <KeyRound size={20} className="text-secondary" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              {...register("otp", {
                required: "Verification code is required",
                pattern: { value: /^\d{6}$/, message: "The code is 6 digits" },
              })}
              className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.otp ? "border-danger" : ""}`}
              disabled={isSubmitting}
            />
          </InputGroup>
          {errors.otp && (
            <small className="text-danger d-block mt-1 ms-1">{errors.otp.message}</small>
          )}
        </div>

        {/* Password */}
        <div>
          <InputGroup>
            <InputGroup.Text
              className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.password ? "border-danger" : ""}`}
            >
              <Lock size={20} className="text-secondary" />
            </InputGroup.Text>
            <Form.Control
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              {...register("password", {
                required: "Password is required",
                minLength: { value: 8, message: "Password must be at least 8 characters" },
                maxLength: { value: 72, message: "Password must be at most 72 characters" },
              })}
              className={`border-start-0 border-end-0 rounded-0 py-2 shadow-none ${errors.password ? "border-danger" : ""}`}
              disabled={isSubmitting}
            />
            <InputGroup.Text
              className={`bg-white border-start-0 rounded-end-pill pe-4 ${errors.password ? "border-danger" : ""}`}
              onClick={() => setShowPassword(!showPassword)}
              style={{ cursor: "pointer" }}
            >
              {showPassword ? (
                <EyeOff size={20} className="text-secondary" />
              ) : (
                <Eye size={20} className="text-secondary" />
              )}
            </InputGroup.Text>
          </InputGroup>
          {errors.password && (
            <small className="text-danger d-block mt-1 ms-1">{errors.password.message}</small>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <InputGroup>
            <InputGroup.Text
              className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.confirmPassword ? "border-danger" : ""}`}
            >
              <Lock size={20} className="text-secondary" />
            </InputGroup.Text>
            <Form.Control
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) => value === password || "Passwords do not match",
              })}
              className={`border-start-0 border-end-0 rounded-0 py-2 shadow-none ${errors.confirmPassword ? "border-danger" : ""}`}
              disabled={isSubmitting}
            />
            <InputGroup.Text
              className={`bg-white border-start-0 rounded-end-pill pe-4 ${errors.confirmPassword ? "border-danger" : ""}`}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ cursor: "pointer" }}
            >
              {showConfirmPassword ? (
                <EyeOff size={20} className="text-secondary" />
              ) : (
                <Eye size={20} className="text-secondary" />
              )}
            </InputGroup.Text>
          </InputGroup>
          {errors.confirmPassword && (
            <small className="text-danger d-block mt-1 ms-1">
              {errors.confirmPassword.message}
            </small>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="mt-3 py-2 rounded-pill fw-bold text-white d-flex align-items-center justify-content-center gap-2"
          style={{ backgroundColor: primaryColor, border: "none" }}
        >
          {isSubmitting ? "Resetting..." : "Reset Password"}
          {isSubmitting && <Spinner animation="border" size="sm" />}
        </Button>
      </motion.form>

      <motion.div custom={3} variants={itemVariants} className="text-center mt-3">
        <Button
          variant="link"
          className="p-0 me-3"
          onClick={handleResend}
          disabled={isResending || isSubmitting}
          style={{ color: primaryColor, textDecoration: "none" }}
        >
          Resend code
          {isResending && <Spinner animation="border" size="sm" className="ms-2" />}
        </Button>
        <Button
          variant="link"
          className="p-0"
          onClick={() => (onBack ? onBack() : navigate("/authentication/login"))}
          style={{ color: "#6b7280", textDecoration: "none" }}
        >
          Use a different number
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default ResetPasswordForm;
